import {
  FilterParameters,
  Hotel,
  MatchedDim,
  Offer,
  Promos,
  SplitBookingBundle as SapiSplitBookingBundle,
  SplitBookingOffer
} from '@findhotel/sapi'
import {Metric, MetricType} from 'web-vitals'

import {
  BookingStatus,
  Source as BookingSource
} from '../../api-types/bovio/response/booking'
import {CognitoError} from '../../auth/types/Cognito'
import {SuggestionItemDataType} from '../../autocomplete/types'
import {ErrorDetails} from '../../booking/types/ErrorDetails'
import {ErrorResponse} from '../../booking/types/ErrorResponse'
import {CancellationPenalty} from '../../offer/types/offer'
import {UnifiedOfferCheckData} from '../../offer/types/OfferCheck'
import {Price} from '../../price/business/price'
import {Room, RoomSplitBookingContext} from '../../room/types/room'

// @todo (@SAPI): move this type/s to the sapi package and import like Offer and Hotel above instead
export type LovHit = {
  categoryID?: number
  id: number
  objectID: string
  value?: string | Record<string, string>
  name?: string | Record<string, string>
}

export enum Source {
  dealFreezeConfirmationPage = 'deal_freeze_confirmation_page',
  menuBar = 'menu_bar',
  mail = 'mail'
}

export type Lov = Record<string, LovHit>

interface SearchLocation {
  country?: string
  region?: string
  city?: string
}

export type AnalyticsContextPayload =
  | MarketingContext
  | SearchContext
  | UserParametersContext
  | HotelContext
  | SearchResultsContext
  | ErrorContext
  | HotelInfoContext
  | PaginationContext
  | PerformanceMeasuredContext
  | SearchHistoryContext
  | AutocompleteContext
  | ExperimentContext
  | CookieConsentContext
  | LeadContext
  | PriceContext
  | CheckoutOfferContext
  | SearchOfferContext
  | BookingContext
  | ServerErrorContext
  | NpsContext
  | CoreWebVitalsContext
  | BookingContext[]
  | null
  | ParametrizedEventContexts
  | MarketingAutomationContext
  | WidgetSearchContext
  | RoomContext
  | RoomContextList
  | RoomOfferContext
  | RoomSplitBookingContext

export type AnalyticsContextList = Partial<
  Record<AnalyticsContext, AnalyticsContextPayload>
>

export interface TrackEventPayload {
  category: Category
  entity: Entity
  action: Action
  page?: Page | null
  payload?: Record<string, unknown>
  component?: string
  analyticsContext?: AnalyticsContextList
  team?: Team
  id?: string
  metric?: string
  value?: number
  meta?: Record<string, string | number | boolean | undefined>
  unit?: string
}

export interface TrackEventProperties extends TrackEventPayload {
  name?: string
}

/**
 * Team - The team that trigger the event
 * The team name should match the environment variable name which holds the analytics API key and endpoint URL
 * For example, the `REACT_APP_ANALYTICS_RETENTION_API_KEY` environment variable keeps the API key for
 * the `retention` team.
 * The `Default` is used when the team name is not provided in the trackEvent call. Its value is different for search
 * and checkout projects as each of these projects has its own default team.
 */
export enum Team {
  Default = 'default',
  Checkout = 'checkout',
  Retention = 'retention',
  Select = 'select',
  PowerFeatures = 'power_features',
  Search = 'search',
  MobileApp = 'mobile-app',
  Shared = 'shared',
  Attribution = 'attribution'
}

/**
 * Category - what is triggering the event.
 * Do not add to this list.
 */
export enum Category {
  System = 'System',
  User = 'User',
  Validation = 'Validation'
}

/**
 * Entity - what thing did it involve.
 * Add to this list when a new entity is required.
 */
export enum Entity {
  /**
   * Website wide entities
   */
  AppBanner = 'AppBanner',
  Attribution = 'Attribution',
  Authentication = 'Authentication',
  GoogleOneTap = 'GoogleOneTap',
  NotificationOptIn = 'NotificationOptIn',
  Booking = 'Booking',
  Errored = 'Errored',
  Experiment = 'Experiment',
  DatePicker = 'DatePicker',
  DatePickerInput = 'DatePickerInput',
  DatePickerDay = 'DatePickerDay',
  DatePickerSubmitButton = 'DatePickerSubmitButton',
  DatePickerClearDatesButton = 'DatePickerClearDatesButton',
  Page = 'Page',
  CookieBanner = 'CookieBanner',
  CookieConsent = 'CookieConsent',
  CookieConsentManagement = 'CookieConsentManagement',
  Signup = 'Signup',
  Login = 'Login',
  CookieBannerScript = 'CookieBannerScript',
  CustomerIoScript = 'CustomerIoScript',
  FullScreenImageGallery = 'FullScreenImageGallery',
  FullScreenRoomImageGallery = 'FullScreenRoomImageGallery',
  ImageCategory = 'ImageCategory',
  PasswordlessSend = 'PasswordlessSend',
  Screenshot = 'Screenshot',
  NpsSurvey = 'NpsSurvey',
  NpsScore = 'NpsScore',
  OptimizeRoomsInfo = 'OptimizeRoomsInfo',
  BookingHistoryPage = 'BookingHistoryPage',
  BookingDetailsButton = 'BookingDetailsButton',
  MenuItem = 'MenuItem',
  BackButton = 'BackButton',
  Picker = 'Picker',
  PriceTimeOut = 'PriceTimeOut',
  CustomerSupportHelp = 'CustomerSupportHelp',
  OurTerms = 'OurTerms',
  ProviderTerms = 'ProviderTerms',
  RoomOfferSoldOut = 'RoomOfferSoldOut',

  /**
   * Search specific entities
   */
  AppExclusiveOffer = 'AppExclusiveOffer',
  BookOnAppConfirmation = 'BookOnAppConfirmation',
  Search = 'Search',
  Offer = 'Offer',
  LockedOffer = 'LockedOffer',
  PreferredRateOffer = 'PreferredRateOffer',
  SearchSummary = 'SearchSummary',
  SearchBox = 'SearchBox',
  SearchBoxSearchButton = 'SearchBoxSearchButton',
  SearchBoxShadow = 'SearchBoxShadow',
  PreferencesOverlay = 'PreferencesOverlay',
  AccountOverlay = 'AccountOverlay',
  ReferralOverlay = 'ReferralOverlay',
  GuestPicker = 'GuestPicker',
  HotelDetails = 'HotelDetails',
  HotelDetailsOverlay = 'HotelDetailsOverlay',
  HomePage = 'HomePage',
  HomePageSearch = 'HomePageSearch',
  DestinationPickerInput = 'DestinationPickerInput',
  GuestPickerInput = 'GuestPickerInput',
  GuestPickerSubmitButton = 'GuestPickerSubmitButton',
  PopupOnExit = 'PopupOnExit',
  LoadMoreResultsButton = 'LoadMoreResultsButton',
  SearchResultsEnd = 'SearchResultsEnd',
  SearchResultsPageSearch = 'SearchResultsPageSearch',
  SearchResultsPageEnd = 'SearchResultsPageEnd',

  TrustSurveyBanner = 'TrustSurveyBanner',
  TrustSurveyBannerResponse = 'TrustSurveyBannerResponse',
  DestinationPickerSearchHistoryItem = 'DestinationPickerSearchHistoryItem',
  DestinationPickerAutocompleteItem = 'DestinationPickerAutocompleteItem',
  DestinationPickerNearbyOption = 'DestinationPickerNearbyOption',
  DestinationPickerAutoCompleteItems = 'DestinationPickerAutoCompleteItems',
  FloatingSearchControls = 'FloatingSearchControls',
  GeolocationCoordinatesFetch = 'GeolocationCoordinatesFetch',
  MapViewButton = 'MapViewButton',
  MapViewToggle = 'MapViewToggle',
  MapViewExpander = 'MapViewExpander',
  MapView = 'MapView',
  MapViewHotelPin = 'MapViewHotelPin',
  MapBox = 'MapBox',
  MapViewSearchAreaButton = 'MapViewSearchAreaButton',
  HotelCard = 'HotelCard',
  HotelCardRemove = 'HotelCardRemove',
  HotelCardAmenityTags = 'HotelCardAmenityTags',
  DeleteAccountOverlay = 'DeleteAccountOverlay',
  DeleteAccountCancel = 'DeleteAccountCancel',
  DeleteAccountConfirm = 'DeleteAccountConfirm',
  LanguageCurrencyPreferencesButton = 'LanguageCurrencyPreferencesButton',
  CurrencyChange = 'CurrencyChange',
  LanguageChange = 'LanguageChange',
  MagicLinkCode = 'MagicLinkCode',
  MagicLinkSend = 'MagicLinkSend',
  MagicLinkResend = 'MagicLinkResend',
  FilterBarFilterButton = 'FilterBarFilterButton',
  FilterPreference = 'FilterPreference',
  SortButton = 'SortButton',
  AvailabilityPaneTile = 'AvailabilityPaneTile',
  AvailabilityPaneSeeMore = 'AvailabilityPaneSeeMore',
  AvailabilityCalendarDay = 'AvailabilityCalendarDay',
  MobileDealPopUp = 'MobileDealPopUp',
  PriceToggle = 'PriceToggle',
  OfferTag = 'OfferTag',
  AnchorHotelAvailablePane = 'AnchorHotelAvailablePane',
  SplitBookingOffers = 'SplitBookingOffers',
  MobileAllOffersSplitBookingOffersModal = 'MobileAllOffersSplitBookingOffersModal',
  SplitBookingInfoIcon = 'SplitBookingInfoIcon',
  GuestConfigPreference = 'GuestConfigPreference',
  PriceDisplayPreference = 'PriceDisplayPreference',
  AppInstallTouchPoint = 'AppInstallTouchPoint',
  NoRoomsAvailableMessage = 'NoRoomsAvailableMessage',
  PartialResultsBanner = 'PartialResultsBanner',
  ReviewOverlay = 'ReviewOverlay',
  ReviewDetails = 'ReviewDetails',
  RoomImage = 'RoomImage',
  DestinationPickerFreeTextSearchItem = 'DestinationPickerFreeTextSearchItem',
  HotelCardImageCarousel = 'HotelCardImageCarousel',
  SearchDateToolTip = 'SearchDateToolTip',
  VioClickId = 'VioClickId',
  PriceRefresh = 'PriceRefresh',
  ReviewDetailsLoadMoreButton = 'ReviewDetailsLoadMoreButton',
  Skeleton = 'Skeleton',
  CarouselSeeAllButton = 'CarouselSeeAllButton',
  HopperInlineAd = 'HopperInlineAd', // Added as part of 7096f519-hopper-ads
  HopperCarousel = 'HopperCarousel', // Added as part of 7096f519-hopper-ads
  HowSearchWorksText = 'HowSearchWorksText',
  AiReviewSummaryFeedback = 'AiReviewSummaryFeedback',
  AiReviewSummarySection = 'AiReviewSummarySection',
  CallToBook = 'CallToBook',
  HotelStructuredDataMarkup = 'HotelStructuredDataMarkup', // Added as part of 46507954-hotel-price-structured-data,
  AnchorHotelHiddenBannerCta = 'AnchorHotelHiddenBannerCta', // Added as part of sapi4eva-anchor-offer-level-filters experiment
  TrustpilotBanner = 'TrustpilotBanner',
  TrustpilotReviewsCount = 'TrustpilotReviewsCount',
  TrustpilotAllReviewsButton = 'TrustpilotAllReviewsButton',
  ExpediaBackTab = 'ExpediaBackTab', // Added as part of 2a7f5ff0-expedia-back-tab experiment
  /**
   * Checkout specific entities
   */
  CurrencySelector = 'CurrencySelector',
  CancellationTermsLabel = 'CancellationTermsLabel',
  PaymentPage = 'PaymentPage',
  PaymentButton = 'PaymentButton',
  PaymentFormInput = 'PaymentFormInput',
  ConfirmationPage = 'ConfirmationPage',
  MembershipStatus = 'MembershipStatus',
  ItineraryPage = 'ItineraryPage',
  DueAtProperty = 'DueAtProperty',
  TaxesAndFees = 'TaxesAndFees',
  LocalTax = 'LocalTax',
  ResortFees = 'ResortFees',
  OtherFees = 'OtherFees',
  PropertyFees = 'PropertyFees',
  PersonalDetails = 'PersonalDetails',
  BillingDetails = 'BillingDetails',
  PaymentDetails = 'PaymentDetails',
  CardCountry = 'CardCountry',
  Cancellation = 'Cancellation',
  CancelBookingButton = 'CancelBookingButton',
  CancellationIntentButton = 'CancellationIntentButton',
  KeepBookingButton = 'KeepBookingButton',
  BookAgainButton = 'BookAgainButton',
  ManageBookingButton = 'ManageBookingButton',
  TrustpilotWidget = 'TrustpilotWidget',
  ReviewOnTPButton = 'ReviewOnTPButton',
  GemsSavingsBanner = 'GemsSavingsBanner',
  FeesDetailsButton = 'FeesDetailsButton',
  FeatureOnboarding = 'FeatureOnboarding',
  CancellationDetailsButton = 'CancellationDetailsButton',
  DealDetailsButton = 'DealDetailsButton',
  RoomDetailsButton = 'RoomDetailsButton',
  MoreDetailsButton = 'MoreDetailsButton',
  SplitBookingDetails = 'SplitBookingDetails',
  PriceTimerDetails = 'PriceTimerDetails',
  TextboxAutofill = 'TextboxAutofill',
  PaymentMethods = 'PaymentMethods',
  PaymentMethodChange = 'PaymentMethodChange',
  SpecialRequestButton = 'SpecialRequestButton',
  ScrollDownButton = 'ScrollDownButton',
  RedirectOffersBox = 'RedirectOffersBox',
  ConfirmationLoadingScreen = 'ConfirmationLoadingScreen',
  HotelInformation = 'HotelInformation',
  /**
   * Booking Core Experience specific entities
   */
  HotelDetailsStaticOfferCTA = 'HotelDetailsStaticOfferCTA',
  HeaderCTA = 'HeaderCTA',
  HotelMoreDealsOption = 'HotelMoreDealsOption',
  RoomDetailsAndOptions = 'RoomDetailsAndOptions',
  HotelDetailsDescription = 'HotelDetailsDescription',
  HotelDetailsReviewsSection = 'HotelDetailsReviewsSection',
  HotelDetailsReviewsTitle = 'HotelDetailsReviewsTitle',
  HotelDetailsLocationSummary = 'HotelDetailsLocationSummary',
  HotelDetailsReviewSummary = 'HotelDetailsReviewSummary',
  HotelDetailsFacilitiesSection = 'HotelDetailsFacilitiesSection',
  HotelDetailsSeeMoreFacilities = 'HotelDetailsSeeMoreFacilities',
  HotelDetailsPropertyPolicySection = 'HotelDetailsPropertyPolicySection',
  HotelDetailsPropertyPolicy = 'HotelDetailsPropertyPolicy',
  HotelDetailsLocationSection = 'HotelDetailsLocationSection',
  HotelDetailsMapButton = 'HotelDetailsMapButton',
  HotelDetailsAddress = 'HotelDetailsAddress',
  SoldOutChangeDatesButton = 'SoldOutChangeDatesButton',
  HotelDetailsBackButton = 'HotelDetailsBackButton',
  HotelDetailsTab = 'HotelDetailsTab',
  HotelDetailsGallery = 'HotelDetailsGallery',
  HotelSave = 'HotelSave',
  HotelSaveMessage = 'HotelSaveMessage',
  HotelSaveMessageViewButton = 'HotelSaveMessageViewButton',
  HotelSaveMessageCloseButton = 'HotelSaveMessageCloseButton',
  ShareLinkButton = 'ShareLinkButton',
  OverlayClose = 'OverlayClose',
  BrowserBackButton = 'BrowserBackButton',
  HotelSaveMessageChangeButton = 'HotelSaveMessageChangeButton',
  PersuasionBannerCloseButton = 'PersuasionBannerCloseButton',
  OfferFilterPreference = 'OfferFilterPreference',
  HotelDetailsSimilarHotelsSection = 'HotelDetailsSimilarHotelsSection',
  SimilarHotels = 'SimilarHotels',
  SeeMoreRoomsButton = 'SeeMoreRoomsButton',
  RoomImageGallery = 'RoomImageGallery',
  RoomOffer = 'RoomOffer',
  RoomSearch = 'RoomSearch',
  RoomsPreheatParams = 'RoomsPreheatParams',
  RoomDeepLink = 'RoomDeepLink',
  SearchResultTimeoutAlert = 'SearchResultTimeoutAlert',
  PriceDifferenceAlert = 'PriceDifferenceAlert',
  SearchResultTimeoutAlertTryAgainButton = 'SearchResultTimeoutAlertTryAgainButton',
  FAQFetch = 'FAQFetch',

  APOverlay = 'APOverlay',
  APStandalone = 'APStandalone',
  AccommodationPage = 'AccommodationPage',
  SelectedDeal = 'SelectedDeal',
  SeeMoreRoomOffersButton = 'SeeMoreRoomOffersButton',
  HotelDetailsFAQItem = 'HotelDetailsFAQItem',
  HotelDetailsFAQSection = 'HotelDetailsFAQSection',
  HotelDetailsFAQPosition = 'HotelDetailsFAQPosition',
  ChatbotSection = 'ChatbotSection',
  ChatbotSendButton = 'ChatbotSendButton',
  ChatbotResponse = 'ChatbotResponse',
  AskChatbotButton = 'AskChatbotButton',
  /**
   * Secure BMP OTP gate specific entities
   */
  OtpResend = 'OtpResend',
  OtpSendCode = 'OtpSendCode',
  /**
   * Membership/Gems specific entities
   */
  SuggestedProperties = 'SuggestedProperties',
  Carousel = 'Carousel',
  CarouselCard = 'CarouselCard',
  SuggestedLocation = 'SuggestedLocations',
  GemsOfferBottomSheet = 'GemsOfferBottomSheet',
  GemsDealTooltip = 'GemsDealTooltip',
  CarouselSeeAll = 'CarouselSeeAll',
  GemsCarousel = 'GemsCarousel',
  AppLink = 'AppLink',
  MyListLink = 'MyListLink',
  CheapestAnchorOffer = 'CheapestAnchorOffer',
  PriceAlertToggle = 'PriceAlertToggle',
  PriceAlertEnable = 'PriceAlertEnable',
  PriceAlertPrompt = 'PriceAlertPrompt',
  SubscribeToPush = 'SubscribeToPush',
  AuthenticationOverlay = 'AuthenticationOverlay',
  SubscribeToPriceWatch = 'SubscribeToPriceWatch',
  PriceMismatchBottomSheet = 'PriceMismatchBottomSheet',
  PriceMismatchViewDeal = 'PriceMismatchViewDeal',
  PriceMismatchNewSearch = 'PriceMismatchNewSearch',
  PriceMismatch = 'PriceMismatch',
  EmailAuthentication = 'EmailAuthentication',
  OTPAuthentication = 'OTPAuthentication',
  UnsubscribeSurvey = 'UnsubscribeSurvey',
  PriceTrendChartToggle = 'PriceTrendChartToggle',
  BetterValueCarousel = 'BetterValueCarousel',
  BetterValueHotelCard = 'BetterValueHotelCard',
  BetterValueSeeMore = 'BetterValueSeeMore',
  PriceInsights = 'PriceInsights',
  PriceInsightsPreview = 'PriceInsightsPreview',
  PriceInsightsCTA = 'PriceInsightsCTA',
  PriceInsightsTooltip = 'PriceInsightsTooltip',
  /**
   * Referral specific entities
   */
  ReferralMemberTierUpgrade = 'ReferralMemberTierUpgrade',
  ReferralLinkShare = 'ReferralLinkShare',
  SavingsBanner = 'SavingsBanner',
  SavingsSection = 'SavingsSection',

  /**
   * Core Web Vitals specific entities
   */
  WebVitals = 'WebVitals',

  /**
   * Mobile App specific entities
   */
  WebView = 'WebView',
  PushConsentDeny = 'PushConsentDeny',
  PushConsentAllow = 'PushConsentAllow',
  PushToggle = 'PushToggle',
  PushPermission = 'PushPermission',
  AppTrackingTransparencyPrompt = 'AppTrackingTransparencyPrompt',
  AppTrackingTransparencyPromptAllow = 'AppTrackingTransparencyPromptAllow',
  AppTrackingTransparencyPromptDeny = 'AppTrackingTransparencyPromptDeny',
  ReviewFlow = 'ReviewFlow',
  BottomBarItem = 'BottomBarItem',
  Onboarding = 'Onboarding',
  AppReview = 'AppReview',
  DeepLink = 'DeepLink',
  AppForceUpdateDialog = 'AppForceUpdateDialog',

  /**
   * Widgets specific entities
   */
  Widget = 'Widget',
  WidgetSoldOut = 'WidgetSoldOut',
  WidgetSearch = 'WidgetSearch',
  WidgetNearbyPlacesSearch = 'WidgetNearbyPlacesSearch',
  SeeAllStays = 'SeeAllStays',
  VioLogo = 'VioLogo',
  Banner = 'Banner',
  PlaceSuggestion = 'PlaceSuggestion',

  /**
   * Deal Freeze specific entities
   */
  DealFreeze = 'DealFreeze',
  DealFreezeButton = 'DealFreezeButton',
  DealFreezeOfferButton = 'DealFreezeOfferButton',
  DealFreezeOverlay = 'DealFreezeOverlay',
  DealFreezeConfirmationOverlay = 'DealFreezeConfirmationOverlay',
  DealFreezeEducationBanner = 'DealFreezeEducationBanner',
  DealFreezeBanner = 'DealFreezeBanner',
  DealFreezeOnboarding = 'DealFreezeOnboarding',
  DealFreezeGuestDetails = 'DealFreezeGuestDetails',
  ToastMessage = 'ToastMessage',
  Tooltip = 'Tooltip',

  /**
   * Browser specific entities
   */
  ContextMenu = 'ContextMenu'
}

/**
 * Action - what did the System/User/Validation do.
 * Do not add to this list unless really necessary.
 */
export enum Action {
  Served = 'Served',
  Clicked = 'Clicked',
  Loaded = 'Loaded',
  Errored = 'Errored',
  Succeeded = 'Succeeded',
  Submitted = 'Submitted',
  Displayed = 'Displayed',
  Viewed = 'Viewed',
  PerformanceMeasured = 'PerformanceMeasured',
  MouseOut = 'MouseOut',
  MouseOvered = 'MouseOvered',
  Scrolled = 'Scrolled',
  Requested = 'Requested',
  Cancelled = 'Cancelled',
  Closed = 'Closed',
  Changed = 'Changed',
  Redirected = 'Redirected',
  Resumed = 'Resumed',
  Triggered = 'Triggered'
}

/**
 * Page - where did the event happen.
 * Do not add to this list unless really necessary.
 */
export enum Page {
  Home = 'Home',
  Search = 'Search',
  ProviderRedirect = 'ProviderRedirect',
  Account = 'Account',
  Membership = 'Membership',
  RoomSelection = 'RoomSelection',
  Payment = 'Payment',
  Confirmation = 'Confirmation',
  Itinerary = 'Itinerary',
  Accommodation = 'Accommodation',
  AlertManagement = 'AlertManagement',
  BookingHistoryPage = 'BookingHistoryPage',
  MyDealFreezePage = 'MyDealFreezePage',
  Profile = 'Profile',
  AccommodationOverlay = 'AccommodationOverlay',
  SEMSearch = 'SEMSearch',
  SearchResults = 'SearchResults',
  RedirectBack = 'RedirectBack',
  Destination = 'Destination',
  NotificationPreferences = 'NotificationPreferences',
  NpsSurvey = 'NpsSurvey'
}

/**
 * Analytics - what data is required in the event.
 * Add to this list when a new context is required.
 */
export enum AnalyticsContext {
  SegmentContext = 'segmentContext',
  UserParametersContext = 'userParametersContext',
  MarketingContext = 'marketingContext',
  SearchContext = 'searchContext',
  HotelContext = 'hotelContext',
  HotelInfoContext = 'hotelInfoContext',
  OfferContext = 'offerContext',
  SearchResultsContext = 'searchResultsContext',
  AutocompleteContext = 'autocompleteContext',
  SearchHistoryContext = 'searchHistoryContext',
  PerformanceMeasuredContext = 'performanceMeasuredContext',
  PaginationContext = 'paginationContext',
  ErrorContext = 'errorContext',
  ExperimentContext = 'experimentContext',
  CookieConsentContext = 'cookieConsentContext',
  PriceContext = 'priceContext',
  LeadContext = 'leadContext',
  BookingContext = 'bookingContext',
  ServerErrorContext = 'serverErrorContext',
  NpsContext = 'npsContext',
  RoomContext = 'roomContext',
  WidgetSearchContext = 'widgetSearchContext',
  RoomContextList = 'roomContextList',
  MarketingAutomationContext = 'marketingAutomationContext',
  SplitBooking = 'splitBooking'
}

/**
 * Enum for performance metrics
 */
export enum PerformanceMetric {
  // First Relevant Paint
  FRP = 'FRP',
  // Most Relevant Paint
  MRP = 'MRP',
  // Largest Relevant Paint
  // @deprecated - use MRP instead
  LRP = 'LRP'
}

export interface MarketingContext {
  label: string
  utmCampaign: string
  utmMedium: string
  utmSource: string
  clickId?: string
  subBrand?: string
}

export interface SearchContext {
  checkIn: string
  checkOut: string
  isDefaultDates: boolean
  isNoDates: boolean
  currency: string
  locale: string
  rooms: string
  numberOfAdults: number
  numberOfChildren: number
  anchorHotelId: string
  placeId: string
  searchId: string
  isUserSearch: boolean
  isHomeSearch: boolean

  isDestinationPageSearch: boolean
  isFsmr: boolean
  isOptimizeRooms: boolean
  isMapOpen: boolean
  isHotelDetailsOpen: boolean
  sortField: string
  sortOrder: string
  filters: FilterParameters
  cugDeals?: string
  mapAreaSearchCoordinates: Array<string | number>
  searchWillRetriggerIfNoOffers: boolean // TODO (@Search) [2022-08-01] Clean up as part of c9457bea-remove-date-check-logic
  isOffersOnlyFetch: boolean
  layout: string
  priceDisplay: 'total_price' | 'nightly_price'
  searchType: string
  searchTrigger: string
  includeTaxes: boolean
  includeLocalTaxes: boolean
  searchLocation: SearchLocation
  mlos?: number
  metaCheckIn: string
  metaCheckOut: string
  address: string
  hotelsHaveStaticPosition?: boolean
  freeTextSearchString?: string
  searchBoxDestinationDisplayValue: string
  datesType?: string
}

export interface WidgetSearchContext {
  checkIn?: string
  checkOut?: string
  placeId?: string
  placeName?: string
  country?: string
  city?: string
  hotelNames?: string[]
  hotelIds?: string[]
  hotelIdsProvider?: string
  rooms?: string
  language?: string
  currency?: string
  userDevice?: string
  userCountry?: string
}

export interface UserParametersContext {
  deviceType: string
  userCountry: string
  userCountryRegion?: string
  userCity?: string
  viewPortHeight: number
  viewPortWidth: number
  anonymousId?: string
  deviceTime?: string
  preferredColorScheme?: string
  isReturningUser?: boolean
  firstVisit?: string
  fullStorySessionUrl?: string
  isNativeAppWebView?: boolean
  currency: string
  locale: string
  visitDayCount: number
  urlLang?: string
  cookieLang?: string
  browserLang?: string
  isUserLoggedIn?: boolean
  userId?: string
  userTier?: string[]
  brand?: string
  emailDomain?: string
}

export interface SplitBookingHotelContext {
  url?: string
  bundleId?: string
  offers?: SplitBookingOffer[]
  tags?: string[]
  totalRate?: RateBreakdown
  splitBookingPosition?: number
  isHidden?: boolean
  isAppLocked?: boolean
}

export interface HotelContext {
  hotelId: string
  highestVisiblePrice: number
  hotelPosition: number
  regularPriceRange?: [number, number]
  hotelTags: string[]
  cheapestPrice: number
  isHotelMapOpen: boolean
  indexedDiscountModifier?: string
  discount?: {
    modifier: string
    hasParityProvider: boolean
    hasDiscountProvider: boolean
    discountProvider: string
  }
  distanceFromSearchedLocation: number
  isPartiallyMatched: boolean
  isAIDescriptionEligible?: boolean
  promos?: Promos
  splitBooking?: SplitBookingHotelContext
  sourceHotelId?: string
  securityDeposit?: number | null
  securityDepositCurrency?: string | null
  hasSecurityDeposit?: boolean
  hasFreeze?: boolean
  displayedBadges?: string[]
}

export interface HotelMetaData extends HotelContext {
  topOffers: SearchOfferContext[]
}

export interface PaginationContext {
  pageOffset: number
  pageNumber: number
  pageLength: number
}

export interface SearchResultsContext extends PaginationContext {
  algoliaNbHotels: number
  anchorHotelHasPrivateDeal: boolean
  anchorHotelHasYourChoice: boolean
  hotelIndexesWithPrivateDeals: number[]
  hotelsMetaData: HotelMetaData[]
  nbHotels: number
  staticHotelsLength: number
  staticHotelsOffset: number
  algoliaTotalNbHotels: number
  unavailableHotels: Array<Record<string, unknown>>
}

export interface HotelInfoContext {
  hotelId: string
  hotelName: string
  address: string
  checkInTime: string
  checkOutTime: string
  country: string
  lastBooked: number
  reviewCount: number
  starRating: number
  guestRating: Hotel['guestRating']
  guestType: Hotel['guestType']
  numberOfImages: number
  topAmenities: LovHit[]
  imageURIs: string[]
}

export interface ErrorContext {
  errorMessage?: string | CognitoError | Record<string, unknown>
  errorType: string
  errorCode?: string
  errorDetails?: ErrorDetails | Record<string, unknown>
  endpoint?: string
}

export interface PerformanceMeasuredContext {
  metric: string
  value: number
  unit: string
}

export type CoreWebVitalsContext = Omit<MetricType, 'name'> & {
  metric: Metric['name']
}

export interface SearchHistoryContext {
  checkIn: string
  checkOut: string
  hotelId?: string
  placeId?: string
  rooms: string
}

export interface AutocompleteContext {
  suggestion: SuggestionItemDataType
  position: number
  inputtedString?: string
}

export interface ExperimentContext {
  experimentId: string
  variationId: string
  isActive: boolean
}

export interface CookieConsentContext {
  consentId: string
  enabledCookieGroups?: string[]
  cookieGroup?: string
  enabled?: boolean
}

export interface LeadContext {
  anonymousId: string
  checkIn: string
  checkOut: string
  currency?: string
  hotelId: string
  searchId: string | null
  locale?: string
  providerCode: string
  providerRateType?: string
  redirectId: string
  rooms: string
  securityDeposit?: number | null
  securityDepositCurrency?: string | null
  hasSecurityDeposit?: boolean
}

export interface PriceContext {
  totalBasePrice?: number
  chargeableCurrency?: string | null
  chargeableTotalPrice: number | null
  currency?: string
  displayPricePerRoomPerNight: number | null
  offerId: string
  totalDueAtProperty: number | null
  totalPrice: number | null
  totalUpFront: number | null
  propertyCurrencyCode?: string
  rooms: number
  nights: number
  totalTaxesAndFees: number
  taxesAndFees?: Array<{
    type: string
    total: number
    isPayAtProperty: boolean
  }>
}

export enum ChargeType {
  PayLater = 'pay_later',
  Default = 'default',
  DealFreeze = 'deal_freeze',
  ChargeLater = 'charge_later'
}

export interface BaseOfferContext {
  anchorPrice?: number
  availableRooms?: number
  bookURI: string
  cancellationPenalties?: Offer['cancellationPenalties']
  cancellationPolicy?: {
    freeRefundableUntil: string | null
    description: string | null
  }
  canPayLater: boolean
  cug?: string[]
  displayPricePerRoomPerNight: number | null
  hasFreeCancellation: boolean
  isPrivateDeal?: boolean
  meals: string[]
  offerId: string
  offerPosition?: number | string | null
  providerCode: string
  providerFeedId?: string | null
  proxyProviderCode: string
  providerRateType?: string
  roomName?: Room['name']
  totalPrice: number | null
  chargeType?: ChargeType[]
  offerToken?: string | null
  sourceComponent?: string
  displayPrice?: number
  /**
   * Expiration time for the Deal Freeze and Charge Later
   */
  paymentExpiryAt?: PaymentExpiryAt
  isSplitBooking?: boolean
}

export interface CheckoutOfferContext extends BaseOfferContext {
  roomId?: string
  roomMasterId?: string
  splitBooking?: UnifiedOfferCheckData | null
  bundleId?: string | null
  originalOfferToken: string | null
}

interface RateBreakdown {
  baseRate: number
  localTaxes: number
  taxes: number
}

export interface SearchOfferContext extends BaseOfferContext {
  displayedTags: string[]
  displayedTagDiscount?: number
  isAnchorPriceOffer: boolean
  isAppExclusiveRate?: boolean
  isCheapest: boolean
  isCheapestRAA: boolean
  isSharedRoom?: boolean
  isTopOffer: boolean
  offerTags: string[]
  rateBreakdown: RateBreakdown
  numberOfRooms: number
  originalAccessTier?: string
  isStatic?: boolean
  osoOfferPosition?: string | number
  splitBooking?: SapiSplitBookingBundle
  bundleId?: string
  ctaType?: string
  isAuthMovedToAP?: boolean
  isEXPMemberRateAvailable?: boolean
}

interface PaymentExpiryAt {
  /**
   * Charge date for Charge Later
   */
  chargeExpiryAt?: string
  /**
   * Expiration date for Deal Freeze
   */
  freezeExpiryAt?: string
}

export interface BookingContext {
  /**
   * The status of the booking request, possible values are confirmed, cancelled, error, frozen, freeze_cancelled and reeze_expired
   */
  status: BookingStatus

  /**
   * The cancellation penalties applied to the {@link Offer} booked in case of cancellation
   */
  cancellationPenalties: CancellationPenalty[]
  /**
   * The amount we displayed to the user as the per room per night
   */
  displayPricePerRoomPerNight: number | null
  /**
   * Whether you have or not free cancellation on your booking
   */
  hasFreeCancellation: boolean
  /**
   * The name of the room booked
   */
  roomName: Room['name']
  /**
   * The total amount the user will pay
   */
  totalPrice?: number | null
  /**
   * It's FH's confirmation ID that can be used by FH customer support.
   * In the BoFH API it is called `confirmationId`.
   * We use a different name so it matches the naming on Looker and EvAS
   */
  bookingId: string
  /**
   * The confirmation ID that a provider needs in their customer support
   */
  providerConfirmationId: string
  /**
   * The ID that FH needs in their customer support
   * TODO: [sc-133585] replace that with providerBookingId when BE is ready
   */
  fhBookingId: string
  /**
   * Used to link tracking events to the same booking from the customer perspective
   *
   */
  customerBookingId: string
  /**
   * The entity responsible for handling the user payment
   */
  merchantOfRecord: string

  /**
   * It shows the booking type if it is a Deal Freeze, or Charge Later
   */
  chargeType?: BookingSource[]
  /**
   * Expiration time for Deal Freeze, and Charge Later.
   */
  paymentExpiryAt?: PaymentExpiryAt
  /**
   * The date and time at which the booking was created
   */
  bookedAt?: string
}

export interface ServerErrorContext {
  /**
   * The HTTP status code of the request.
   * https://umbraco.com/knowledge-base/http-status-codes/
   */
  statusCode: number
  /**
   * Which params were passed to the request
   */
  params?: Record<string, unknown>
  /**
   * Which headers were passed to the request
   */
  headers: Record<string, unknown>
  /**
   * Which url was used in the request
   */
  url?: string
  /**
   * Which HTTP method was used for the request
   * https://developer.mozilla.org/en-US/docs/Web/HTTP/Methods
   */
  method?: string
  /**
   * An object containing details about the error that happened
   */
  errorResponse: ErrorResponse | Record<string, unknown>
  /**
   * A descriptive version of the error that happened
   */
  errorMessage: string
  /**
   * Whether the user will be able to keep using the app of the failing of this request means the appplication
   * is rendered useless.
   */
  recoverableError?: boolean
}

export interface NpsContext {
  /**
   * The score given by the user to a NPS survey
   */
  npsScore: number
  /**
   * Represents the question asked to the user after a score is registered. The question usually depends on the score given
   */
  openQuestion?: string
  /**
   * Represents NPS survey scale max value if it's differs from default (11)
   */
  npsScale?: number
  /**
   * Represents the maximum score a user can submit
   */
  npsMaxScore?: number
}

export interface RoomOfferContext {
  roomName: string
  roomMasterId: string
  roomPosition: number | undefined
  roomId: string
  numberOfImages: number
  roomAmenities: string[]
  roomDescription: string
  squashedIds: string[]
  displayPricePerRoomPerNight: number
  isPrivateDeal: boolean
  isAppExclusiveRate: boolean
  offerId: string
  totalPrice: number | null
  anchorPrice: number
  offerTags: string[] | undefined
  availableRooms: number
  canPayLater: boolean | undefined
  hasFreeCancellation: boolean
  providerCode: string
  cancellationPenalties: CancellationPenalty[]
  cug: string[]
  providerFeedId: string
  providerRateType?: string
  isSelectedOffer: boolean | undefined
  matchType: 'exact' | 'partial' | 'by_terms' | 'by_price' | undefined
  matchedDim?: MatchedDim
  matchedOfferPriceDiff?: number
  services: string[]
  payAtProperty: boolean
  paymentExpiryAt?: PaymentExpiryAt
  chargeType?: ChargeType[]
  offerPosition: number
  offerName?: string
  proxyProviderCode?: string
  isRoomOffer?: boolean
  offerToken?: string
  roomSearchId?: string
  ctaType?: string
  // Price breakdown fields
  totalBasePrice?: number
  totalTaxesAndFees?: number
  taxesAndFees?: Array<{
    type: string
    total: number
    isPayAtProperty: boolean
  }>
  totalUpFront?: number
  totalDueAtProperty?: number
  // Should be implemented as part of sc-104456
  // isCheapest: boolean
}

// Context that is used for marketing automation purposes (Customer.io)
export interface MarketingAutomationContext {
  hotelName?: string
  hotelImageUrl?: string
  bookingManagementUrl?: string
  paymentUrl?: string
  hotelAddress?: string
  price?: Price
  numberOfNights?: number
  totalGuests?: number
  starRating?: number
  nightlyPrice?: number
}

type ParametrizedEventContexts =
  | ParametrizedHotelContext
  | ParametrizedHotelInfoContext

export interface ParametrizedHotelContext {
  contextType: AnalyticsContext.HotelContext
  hotelId: Hotel['objectID']
}

export interface ParametrizedHotelInfoContext {
  contextType: AnalyticsContext.HotelInfoContext
  hotelId: Hotel['objectID']
}

export interface RoomContext {
  numberOfImages: number
  roomAmenities: string[]
  keyAmenities: string[]
  size: Room['area'] | null
  occupancy: Room['occupancy'] | null
  bedConfig: NonNullable<Room['bedrooms']>[number]['bedConfigurations'] | null
  roomPosition: number
  providerRoomName: string
  displayedRoomName: string
  /** Name the guest sees, generated for roomless v2 rooms */
  displayRoomName?: string
  roomId: string
  roomMasterId?: string
  squashedIds: string[]
  isSelectedRoom: boolean
  offers: RoomOfferContext[]
  roomSearchId?: string
}

export type RoomContextList = RoomContext[]

/**
 * How the user triggered a click event. Right, middle and long-press clicks
 * navigate without firing a plain `click`, so they need tracking of their own.
 */
export enum ClickType {
  LeftClick = 'LeftClick',
  MiddleClick = 'MiddleClick',
  RightClick = 'RightClick',
  LongPress = 'LongPress'
}
